// Workshop wire types and message builders.
//
// Extracted from messages.ts to respect the §10 400-line cap.
// Re-exported from messages.ts — import from either file.
//
// Envelope structure is inlined (no import from messages.ts) to avoid a
// circular dependency. The resulting types are structurally identical to
// Envelope<T, P> and satisfy the discriminated union in ServerMessage /
// ClientMessage.

// -----------------------------------------------------------------------
// Workshop data types (wire contract)
// -----------------------------------------------------------------------

export type JobStatus = "queued" | "running" | "completed" | "cancelled";

export type WorkshopJob = {
  jobId: string;
  recipeId: string;
  startedAt: number;
  completesAt: number;
  status: JobStatus;
  // Present only when status === "completed". count is always 1 in Phase 3
  // (recipes yield singletons); included as a forward-compatibility field.
  output?: { itemId: string; count: number; quality: number };
};

export type WorkshopState = {
  jobs: WorkshopJob[];              // queued + running, ordered by completesAt
  completedUnclaimed: WorkshopJob[]; // completed but not yet claimed by the player
};

// -----------------------------------------------------------------------
// Workshop message envelope types
// -----------------------------------------------------------------------

// Local Envelope alias — same shape as shared/messages.ts's export,
// inlined to avoid the cycle. seq/eventId are Phase 5 Step 3 durable
// wire augmentation (present on server→client durables only).
type Envelope<T extends string, P> = {
  type: T;
  ts: number;
  payload: P;
  seq?: number;
  eventId?: string;
};

// Client → Server (routed directly to PlayerDO, not via ZoneDO)
export type ClientQueueCraft = Envelope<"queueCraft", { recipeId: string; batchCount: number }>;
export type ClientCancelCraft = Envelope<"cancelCraft", { jobId: string }>;
// Phase 7 Block D — payload accepts an optional `jobId` so the workbench
// can claim a single completed job rather than draining the whole tray.
// Existing callers that omit `jobId` (chaos harness, integration tests)
// retain claim-all behavior for backward compatibility.
export type ClientClaimCompletedJobs = Envelope<"claimCompletedJobs", { jobId?: string }>;
// Polled by the client while the workbench is open to pick up alarm-driven
// completions that PlayerDO can't push directly (it doesn't own the WebSocket).
// Server replies with workshopUpdated. See zone-workshop.ts handleGetWorkshop.
export type ClientGetWorkshop = Envelope<"getWorkshop", Record<string, never>>;

// Server → Client (sent from PlayerDO directly to the player's WebSocket).
// completesAt on craftQueued is the EARLIEST job's completion time.
export type ServerCraftQueued = Envelope<
  "craftQueued",
  { jobIds: string[]; completesAt: number; workshop: WorkshopState }
>;
export type ServerCraftCancelled = Envelope<
  "craftCancelled",
  { jobId: string; refunded: { itemId: string; count: number }[]; workshop: WorkshopState }
>;
// Fired when a queued job transitions to running (becomes head of queue).
export type ServerJobStarted = Envelope<
  "jobStarted",
  { jobId: string; recipeId: string; completesAt: number; workshop: WorkshopState }
>;
// Fired when the alarm handler completes a job and rolls quality.
export type ServerJobCompleted = Envelope<
  "jobCompleted",
  { jobId: string; output: { itemId: string; count: number; quality: number }; workshop: WorkshopState }
>;
// Catch-all workshop snapshot for state reconciliation on reconnect.
export type ServerWorkshopUpdated = Envelope<"workshopUpdated", { workshop: WorkshopState }>;

// -----------------------------------------------------------------------
// Builders
// -----------------------------------------------------------------------

export const buildQueueCraftMessage = (recipeId: string, batchCount: number): ClientQueueCraft => ({
  type: "queueCraft",
  ts: Date.now(),
  payload: { recipeId, batchCount },
});

export const buildCancelCraftMessage = (jobId: string): ClientCancelCraft => ({
  type: "cancelCraft",
  ts: Date.now(),
  payload: { jobId },
});

export const buildClaimCompletedJobsMessage = (jobId?: string): ClientClaimCompletedJobs => ({
  type: "claimCompletedJobs",
  ts: Date.now(),
  payload: jobId !== undefined ? { jobId } : {},
});

export const buildGetWorkshopMessage = (): ClientGetWorkshop => ({
  type: "getWorkshop",
  ts: Date.now(),
  payload: {},
});

export const buildCraftQueuedMessage = (
  jobIds: string[],
  completesAt: number,
  workshop: WorkshopState,
): ServerCraftQueued => ({
  type: "craftQueued",
  ts: Date.now(),
  payload: { jobIds, completesAt, workshop },
});

export const buildCraftCancelledMessage = (
  jobId: string,
  refunded: { itemId: string; count: number }[],
  workshop: WorkshopState,
): ServerCraftCancelled => ({
  type: "craftCancelled",
  ts: Date.now(),
  payload: { jobId, refunded, workshop },
});

export const buildJobStartedMessage = (
  jobId: string,
  recipeId: string,
  completesAt: number,
  workshop: WorkshopState,
): ServerJobStarted => ({
  type: "jobStarted",
  ts: Date.now(),
  payload: { jobId, recipeId, completesAt, workshop },
});

export const buildJobCompletedMessage = (
  jobId: string,
  output: { itemId: string; count: number; quality: number },
  workshop: WorkshopState,
): ServerJobCompleted => ({
  type: "jobCompleted",
  ts: Date.now(),
  payload: { jobId, output, workshop },
});

export const buildWorkshopUpdatedMessage = (workshop: WorkshopState): ServerWorkshopUpdated => ({
  type: "workshopUpdated",
  ts: Date.now(),
  payload: { workshop },
});
